import { toast } from "react-toastify";

import apiClient from "../../apis/api-client";
import { getFriendlyErrorMessage } from "../../util/error-message-mapper";
import { BaseApplicationService } from "../../domain/base/base-application-service";
import { Paging, ProcedureTag } from "../../domain/proc-management";
import {
  isTagPriorityDuplicationError,
  isTagDuplicateError,
  reportProcManagementRelatedError,
} from "../../domain/proc-management/util/error";

export type ContextProps = {
  procTags: ProcedureTag[];
  paging: Paging;
  fetchProcTagsByOrgId: (organizationId: string, page?: number) => void;
  createProcTag: (organizationId: string, name: string) => void;
  deleteProcTag: (organizationId: string, tagId: string) => void;
  updateProcTag: (
    id: string,
    organizationId: string,
    name: string,
    priority: number,
  ) => void;
};

export const MaxPriority = 100000;
export const MaxNameLength = 10;

export class BaseProcManagementProcedureTagsListService<
  P extends Record<string, any>,
> extends BaseApplicationService<P & ContextProps> {
  constructor(rest: P & Partial<ContextProps>) {
    super();
    this.state = {
      procTags: [],
      paging: { current: 1, last: 1 },
      fetchProcTagsByOrgId: this.fetchProcTagsByOrgId.bind(this),
      createProcTag: this.createProcTag.bind(this),
      deleteProcTag: this.deleteProcTag.bind(this),
      updateProcTag: this.updateProcTag.bind(this),
      ...rest,
    };
  }

  async fetchProcTagsByOrgId(organizationId: string, page?: number) {
    try {
      const { data } = await apiClient.procManagement.listProcedureTags({
        organizationId,
        page,
      });
      this.setState((prev) => {
        return { ...prev, procTags: data.items, paging: data.paging };
      });
    } catch (error) {
      reportProcManagementRelatedError(error);
      const friendlyMessage = getFriendlyErrorMessage(error);
      toast.error(`手続きタグの取得に失敗しました: ${friendlyMessage}`);
    }
  }

  async createProcTag(
    organizationId: string,
    name: string,
    priority: number,
    suppressOnPortal: boolean,
    pageNum: number,
  ) {
    try {
      await apiClient.procManagement.createProcedureTag({
        organizationId,
        name,
        priority,
        suppressOnPortal: suppressOnPortal,
      });
      toast.success(`手続きタグを作成しました。`);
    } catch (error) {
      if (isTagPriorityDuplicationError(error)) {
        toast.error(
          `表示順が重複しているため、手続きタグの作成に失敗しました。表示順を変更して再度実行してください。`,
        );
        return;
      }
      if (isTagDuplicateError(error)) {
        toast.error(
          `タグ名が重複しているため、手続きタグの作成に失敗しました。タグ名を変更して再度実行してください。`,
        );
        return;
      }
      reportProcManagementRelatedError(error);
      const friendlyMessage = getFriendlyErrorMessage(error);
      toast.error(`手続きタグの作成に失敗しました: ${friendlyMessage}`);
    }
    this.fetchProcTagsByOrgId(organizationId, pageNum);
  }

  async updateProcTag(
    id: string,
    organizationId: string,
    name: string,
    priority: number,
    suppressOnPortal: boolean,
    pageNum: number,
  ) {
    try {
      await apiClient.procManagement.updateProcedureTag({
        id,
        name,
        organizationId,
        priority,
        suppressOnPortal: suppressOnPortal,
      });
      toast.success(`手続きタグを更新しました。`);
    } catch (error) {
      if (isTagPriorityDuplicationError(error)) {
        toast.error(
          `表示順が重複しているため、手続きタグの更新に失敗しました。表示順を変更して再度実行してください。`,
        );
        return;
      }
      if (isTagDuplicateError(error)) {
        toast.error(
          `タグ名が重複しているため、手続きタグの作成に失敗しました。タグ名を変更して再度実行してください。`,
        );
        return;
      }
      reportProcManagementRelatedError(error);
      const friendlyMessage = getFriendlyErrorMessage(error);
      toast.error(`手続きタグの更新に失敗しました: ${friendlyMessage}`);
    }
    this.fetchProcTagsByOrgId(organizationId, pageNum);
  }

  async deleteProcTag(organizationId: string, tagId: string, pageNum: number) {
    try {
      await apiClient.procManagement.deleteProcedureTag(tagId);
      toast.success(`手続きタグを削除しました。`);
    } catch (error) {
      reportProcManagementRelatedError(error);
      const friendlyMessage = getFriendlyErrorMessage(error);
      toast.error(`手続きタグの削除に失敗しました: ${friendlyMessage}`);
    }
    this.fetchProcTagsByOrgId(organizationId, pageNum);
  }
}
